import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Image as ImageIcon, Loader2 } from 'lucide-react';

import { Icon as BrandIcon } from '@/lib/Icon';

import { PageHeader } from '@/components/app-shell';
import { useUploadDocument } from '@/lib/api/hooks';
import { cn } from '@/lib/cn';

interface IdUploadStepProps {
  onUploaded: () => void;
  onSkip: () => void;
  identityGateEnabled?: boolean;
}

// Government-ID capture between intakes and consent. The compounding
// pharmacy (TPH) keeps a copy on file. The patient can skip and pick
// it up later from the home task list — we don't block onboarding here.
//
// We intentionally omit the `capture=` attribute on the file input. On
// iOS/Android omitting it shows the OS sheet with "Take Photo", "Photo
// Library", and "Choose File" — which is the right UX without us
// building a camera. Adding `capture="environment"` would force-launch
// the camera and hide the photo library option.
export function IdUploadStep({
  onUploaded,
  onSkip,
  identityGateEnabled = false,
}: IdUploadStepProps) {
  const upload = useUploadDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewFeedback, setReviewFeedback] = useState<string | null>(null);
  const [confirmSkip, setConfirmSkip] = useState(false);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    setReviewFeedback(null);
    setFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (reviewFeedback) {
      onUploaded();
      return;
    }
    if (!file) return;
    setError(null);
    try {
      const result = await upload.mutateAsync({ file, purpose: 'id' });
      if (result.feedback) {
        setReviewFeedback(result.feedback);
        return;
      }
      onUploaded();
    } catch (err) {
      setError((err as Error).message ?? 'Upload failed');
    }
  };

  const fileSizeLabel = useMemo(() => {
    if (!file) return null;
    if (file.size < 1024) return `${file.size} B`;
    if (file.size < 1024 * 1024) return `${(file.size / 1024).toFixed(1)} KB`;
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }, [file]);

  return (
    <div className="flex-1 flex flex-col">
      <PageHeader
        title="Government ID"
        description="Upload the complete front of your driver's license or state ID, or your passport photo page. Your photo, full name, and date of birth must be readable. The back alone is not sufficient. JPG, PNG, or PDF."
      />
      <div className="flex-1 px-15 md:px-40 pt-20 md:pt-40 pb-30 max-w-[44rem] md:max-w-[64rem] mx-auto w-full flex flex-col gap-20">
        {/* HEIC is listed because the backend converts it (pillow-heif), so
            Mac/desktop users can pick iPhone photos directly. Deliberately
            NOT image/* though — unlisted exotic formats stay blocked, and on
            iOS an explicit list keeps the photo-library picker behavior
            predictable. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
          className="hidden"
          onChange={handleChange}
        />

        <button
          type="button"
          onClick={handlePick}
          disabled={upload.isPending}
          className={cn(
            'flex flex-col items-center justify-center gap-10 py-40 px-20',
            'border-2 border-dashed border-line-default bg-[var(--surface-page)]',
            'hover:border-ink hover:bg-[var(--surface-sunken)]',
            'disabled:opacity-50',
          )}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="ID preview"
              className="max-h-[14rem] max-w-full object-contain"
            />
          ) : file ? (
            <FileText
              size={28}
              strokeWidth={1.5}
              className="text-ink-secondary"
              aria-hidden
            />
          ) : (
            <ImageIcon
              size={28}
              strokeWidth={1.5}
              className="text-ink-muted"
              aria-hidden
            />
          )}
          <div className="type-label-md text-ink">
            {file ? 'Replace photo' : 'Choose photo or PDF'}
          </div>
          {file ? (
            <div className="type-body-sm text-ink-secondary truncate max-w-full">
              {file.name}
              {fileSizeLabel ? ` · ${fileSizeLabel}` : ''}
            </div>
          ) : (
            <div className="type-body-sm text-ink-muted">
              Tap to open camera, photo library, or files
            </div>
          )}
        </button>

        {error && (
          <div role="alert" className="type-body-sm text-danger">
            {error}
          </div>
        )}
        {reviewFeedback && (
          <div role="status" className="type-body-sm text-ink-secondary">
            {reviewFeedback}
          </div>
        )}
      </div>

      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-20 py-15 md:px-40 md:py-20 flex flex-col gap-10 items-center">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || upload.isPending}
          className="w-full flex items-center justify-between h-50 px-20 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50 max-w-[64rem] mx-auto"
        >
          {upload.isPending ? (
            <span className="flex items-center gap-10">
              <Loader2 size={14} className="animate-spin" /> Uploading and
              checking ID…
            </span>
          ) : (
            'Continue'
          )}
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-8"
          >
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
        <button
          type="button"
          onClick={() =>
            identityGateEnabled ? setConfirmSkip(true) : onSkip()
          }
          disabled={upload.isPending}
          className="type-body-sm text-ink-muted hover:text-ink underline-offset-2 hover:underline disabled:opacity-50"
        >
          Skip for now
        </button>
      </div>
      {confirmSkip && (
        <div
          className="fixed inset-0 z-dialog bg-ink/55 p-20 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-id-title"
        >
          <section className="max-w-[48rem] w-full bg-page p-20 border border-line">
            <h2 id="skip-id-title" className="type-heading-md">
              Upload your ID later?
            </h2>
            <p className="type-body-md mt-12">
              Please upload your ID before treatment review. Your clinician
              verifies your identity during prescribing. You can continue
              account setup and upload your ID from the portal when you are
              ready.
            </p>
            <p className="type-body-sm text-ink-muted mt-10">
              Your appointments remain available. A purchase does not authorize
              a prescription.
            </p>
            <div className="flex flex-wrap gap-10 mt-20">
              <button
                type="button"
                className="border border-line px-15 py-10 type-label-sm"
                onClick={() => setConfirmSkip(false)}
              >
                Upload now
              </button>
              <button
                type="button"
                className="bg-ink text-page px-15 py-10 type-label-sm"
                onClick={onSkip}
              >
                Continue without ID
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
